import isNil from 'lodash/isNil';
import omitBy from 'lodash/omitBy';
import { CHALLENGES_TYPE } from 'services/groups/challenges-common-types';
import {
  ANALYTICS_SERVICE_EVENT_NAMES,
  GA_CLICK_EVENT_NAMES
} from './ga_events';
import { v4 as uuidv4 } from 'uuid';
import { isAnalyticsConsented } from './utils';

const VISITOR_ID_STORAGE_KEY = 'visitor_id';

type AnalyticsModule = typeof import('firebase/analytics');

type AnalyticsHandle = {
  analytics: ReturnType<AnalyticsModule['getAnalytics']>;
  mod: AnalyticsModule;
};

type BufferedAnalyticsAction = (handle: AnalyticsHandle) => void;
type AnalyticsConsentListener = (consented: boolean) => void; 

let analyticsPromise: Promise<AnalyticsHandle | null> | null = null;
let analyticsHandle: AnalyticsHandle | null = null;
let isFlushingBufferedActions = false;
// Keep Firebase Analytics behind a lazy import without dropping events emitted
// before that chunk is ready. We capture analytics work synchronously in memory,
// then flush it in FIFO order after initialization so early page views and
// bounce traffic are still recorded if the app stays alive long enough to load.
const bufferedActions: BufferedAnalyticsAction[] = [];
const analyticsConsentListeners = new Set<AnalyticsConsentListener>();

const flushBufferedActions = (handle: AnalyticsHandle) => {
  if (isFlushingBufferedActions) {
    return;
  }

  isFlushingBufferedActions = true;

  try {
    while (bufferedActions.length > 0) {
      const action = bufferedActions.shift();

      action?.(handle);
    }
  } finally {
    isFlushingBufferedActions = false;
  }
};

const getAnalyticsHandle = async (): Promise<AnalyticsHandle | null> => {
  if (typeof window === 'undefined') {
    return null;
  }

  if (analyticsHandle) {
    return analyticsHandle;
  }

  if (!isAnalyticsConsented()) {
    return null;
  }

  if (!analyticsPromise) {
    analyticsPromise = (async () => {
      const [
        { getAnalytics, setAnalyticsCollectionEnabled },
        { getFirebaseApp },
        { default: isPrerenderCrawler }
      ] = await Promise.all([
        import('firebase/analytics'),
        import('./index'),
        import('helpers/isPrerenderCrawler')
      ]);

      if (isPrerenderCrawler()) {
        return null;
      }

      // Use window.gtag directly instead of Firebase's setConsent — setConsent
      // passes "update" as the first arg, which the Firebase gtag wrapper reads
      // as the consent object and discards the real settings.
      (window as any).gtag?.('consent', { analytics_storage: 'granted' });

      const analytics = getAnalytics(getFirebaseApp());
      const mod = await import('firebase/analytics');
      // Explicitly enable collection on every init — covers the re-accept case
      // where a previous session left the Firebase-persisted flag set to false.
      setAnalyticsCollectionEnabled(analytics, true);

      return { analytics, mod };
    })()
      .then(handle => {
        if (handle) {
          analyticsHandle = handle;
          flushBufferedActions(handle);
        } else {
          bufferedActions.length = 0;
        }

        return handle;
      })
      .catch(error => {
        analyticsPromise = null;
        analyticsHandle = null;
        bufferedActions.length = 0;
        console.warn('firebase.analytics: ERROR', error);
        return null;
      });
  }

  return analyticsPromise;
};

const enqueueAnalyticsAction = (action: BufferedAnalyticsAction) => {
  bufferedActions.push(action);

  if (analyticsHandle) {
    flushBufferedActions(analyticsHandle);
    return;
  }

  void getAnalyticsHandle();
};

export const subscribeAnalyticsConsent = (
  listener: AnalyticsConsentListener
): (() => void) => {
  analyticsConsentListeners.add(listener);

  return () => {
    analyticsConsentListeners.delete(listener);
  };
};

const notifyAnalyticsConsentListeners = (consented: boolean) => {
  analyticsConsentListeners.forEach(listener => {
    try {
      listener(consented);
    } catch (error) {
      console.warn('firebase.analytics: consent listener error', error);
    }
  });
};

export const resetAnalyticsOnConsentChange = async (): Promise<void> => {
  const consented = isAnalyticsConsented();

  if (!consented && (analyticsHandle || analyticsPromise)) {
    if (analyticsPromise) {
      await analyticsPromise;
    }
    if (analyticsHandle) {
      // Disables all collection including cookieless modeling pings — consent
      // mode alone (analytics_storage: denied) still allows those through.
      analyticsHandle.mod.setAnalyticsCollectionEnabled(analyticsHandle.analytics, false);
    }
    // Use window.gtag directly instead of Firebase's setConsent — setConsent
    // passes "update" as the first arg, which the Firebase gtag wrapper reads
    // as the consent object and discards the real settings.
    (window as any).gtag?.('consent', { analytics_storage: 'denied' });
    analyticsHandle = null;
    analyticsPromise = null;
    bufferedActions.length = 0;
  }

  notifyAnalyticsConsentListeners(consented);

  if (consented) {
    if (analyticsHandle) {
      (window as any).gtag?.('consent', { analytics_storage: 'granted' });
      // Re-enable collection when handle already exists (e.g. user re-accepts
      // without a page reload) — mirrors the disable call in the deny path above.
      analyticsHandle.mod.setAnalyticsCollectionEnabled(analyticsHandle.analytics, true);
    } else {
      void getAnalyticsHandle();
    }
  }
};

export const getUserPsuedoId = async () => {
  const handle = await getAnalyticsHandle();

  if (!handle) {
    return null;
  }

  const timeout = new Promise<null>(resolve =>
    setTimeout(() => resolve(null), 1000)
  );

  const clientId = await Promise.race([
    handle.mod.getGoogleAnalyticsClientId(handle.analytics),
    timeout
  ]);

  return clientId;
};

export const getOrCreateVisitorId = () => {
  if (typeof window === 'undefined') return undefined;

  try {
    const existingVisitorId = window.localStorage.getItem(
      VISITOR_ID_STORAGE_KEY
    );
    if (existingVisitorId) return existingVisitorId;

    const visitorId = uuidv4();
    window.localStorage.setItem(VISITOR_ID_STORAGE_KEY, visitorId);
    return visitorId;
  } catch {
    return undefined;
  }
};

type LooseObject = {
  [key: string]: any;
};

export interface LogGAEventParams extends LooseObject {
  event_name: string;
  page_name?: string;
  previous_page_name?: string;
  experiment_id?: string;
  variation_id?: string;
  promo_code?: string;
  service_event_name?: ANALYTICS_SERVICE_EVENT_NAMES;
}

export const logGAEvent = (eventDetails: LogGAEventParams): void => {
  if (!isAnalyticsConsented()) {
    return;
  }

  enqueueAnalyticsAction(({ analytics, mod }) => {
    mod.logEvent(analytics, eventDetails.event_name, {
      ...eventDetails,
      user_agent: navigator?.userAgent
    });
  });
};

interface LogPageViewEventParams extends LooseObject {
  page_name: string;
  previous_page_name: string;
}

export const logPageViewEvent = (
  eventDetails: LogPageViewEventParams
): void => {
  if (!isAnalyticsConsented()) {
    return;
  }

  let pageViewEventData: LogPageViewEventParams = {
    ...eventDetails,
    event_name: 'page_view',
    name: 'page_view',
    user_agent: navigator?.userAgent
  };

  pageViewEventData = omitBy(
    pageViewEventData,
    isNil
  ) as LogPageViewEventParams;

  enqueueAnalyticsAction(({ analytics, mod }) => {
    mod.logEvent(analytics, 'page_view', pageViewEventData);
  });
};

export type TRACKING_OBJECT_TYPE =
  | 'LIBRARY_ITEMS'
  | 'EVENT'
  | 'GROUP'
  | 'SHOP_PRODUCT'
  | 'PLAYLISTS'
  | 'PUBLISHERS'
  | 'RETREAT'
  | 'THERAPIST_RESOURCE'
  | 'FOLDER';

export interface LogGAClickEventParams extends LooseObject {
  click_event_name?: GA_CLICK_EVENT_NAMES;
  library_item_id?: string;
  library_item_type?: string;
  live_event_id?: string;
  course_id?: string;
  time_id?: string;
  topic_id?: string;
  retreat_id?: string;
  link?: string;
  challenge_banner_id?: string;
  tracking_object_type?: TRACKING_OBJECT_TYPE;
  item_type?: 'COURSES';
  challenge_template_type?: CHALLENGES_TYPE;
  promo_code?: string;
}

export interface TrackGAServiceEventParams extends LooseObject {
  service_event_name?: ANALYTICS_SERVICE_EVENT_NAMES;
  library_item_id?: string;
  library_item_type?: string;
  live_event_id?: string;
  course_id?: string;
  time_id?: string;
  topic_id?: string;
  retreat_id?: string;
  playlist_id?: string;
  publisher_id?: string;
  group_id?: string;
  folder_id?: string;
  worksheet_id?: string;
  link?: string;
  challenge_banner_id?: string;
  tracking_object_type?: TRACKING_OBJECT_TYPE;
  item_type?: 'COURSES';
  challenge_template_type?: CHALLENGES_TYPE;
  promo_code?: string;
}

export interface AnalyticsUserProperties {
  userId?: string;
  isSubscriber?: boolean;
  visitorId?: string | null;
}

export const setAnalyticsUserProperties = ({
  userId,
  isSubscriber,
  visitorId
}: AnalyticsUserProperties) => {
  if (!isAnalyticsConsented()) {
    return;
  }

  enqueueAnalyticsAction(({ analytics, mod }) => {
    mod.setUserProperties(analytics, {
      is_subscriber: isSubscriber,
      user_id: userId,
      visitor_id: visitorId
    });
  });
};

export default null;
